// Teras pengantarabangsaan (i18n) — dikongsi oleh Worker dan frontend.
// Bahasa utama & lalai: Bahasa Melayu (ms). Setiap bahasa ialah kamus lengkap tersendiri;
// tiada teks dicampur antara bahasa. Kunci yang tiada dalam bahasa dipilih jatuh balik kepada
// bahasa utama (dan ujian memastikan kedua-dua kamus mempunyai kunci yang sama).
//
// Format nilai:
//   'Teks {param}'                 — penggantian parameter
//   { one: '...', other: '...' }   — bentuk jamak (dipilih berdasarkan params.n)
// Parameter bermula dengan '@' diterjemah sebagai kunci: { what: '@entity.booking' }.
// '@kunci|kunciSandaran' menggunakan sandaran jika kunci utama tiada.

import ms from './ms.js';
import en from './en.js';

export const DEFAULT_LOCALE = 'ms';
export const LOCALES = {
  ms: { name: 'Bahasa Melayu', htmlLang: 'ms', dict: flatten(ms) },
  en: { name: 'English', htmlLang: 'en', dict: flatten(en) },
};
export const SUPPORTED_LOCALES = Object.keys(LOCALES);

export function flatten(obj, prefix = '', out = {}) {
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === 'object' && !Array.isArray(v) && !isPlural(v)) flatten(v, key, out);
    else out[key] = v;
  }
  return out;
}

function isPlural(v) {
  return typeof v === 'object' && v !== null && 'other' in v && Object.keys(v).every((k) => ['zero', 'one', 'other'].includes(k));
}

export function normalizeLocale(input) {
  if (!input) return null;
  const s = String(input).toLowerCase().trim();
  if (LOCALES[s]) return s;
  const base = s.split(/[-_]/)[0];
  if (base === 'ms' || base === 'my' || s === 'bm') return 'ms';
  if (LOCALES[base]) return base;
  return null;
}

export function hasKey(locale, key) {
  return Object.prototype.hasOwnProperty.call(LOCALES[locale]?.dict || {}, key);
}

function lookup(locale, key) {
  const loc = LOCALES[locale] || LOCALES[DEFAULT_LOCALE];
  if (Object.prototype.hasOwnProperty.call(loc.dict, key)) return loc.dict[key];
  if (Object.prototype.hasOwnProperty.call(LOCALES[DEFAULT_LOCALE].dict, key)) return LOCALES[DEFAULT_LOCALE].dict[key];
  return undefined;
}

export function translate(locale, key, params = {}) {
  let value = lookup(locale, key);
  if (value === undefined) return key;
  if (typeof value === 'object') {
    const n = Number(params.count ?? params.n);
    value = (n === 0 && value.zero) || (n === 1 && value.one) || value.other;
  }
  return String(value).replace(/\{(\w+)\}/g, (m, name) => {
    if (!(name in params)) return m;
    const p = params[name];
    if (typeof p === 'string' && p.startsWith('@')) {
      const [main, fallback] = p.slice(1).split('|');
      if (lookup(locale, main) !== undefined) return translate(locale, main, params);
      if (fallback) return translate(locale, fallback, params);
      return translate(locale, main, params);
    }
    return p === null || p === undefined ? '' : String(p);
  });
}

export function makeT(locale) {
  const l = normalizeLocale(locale) || DEFAULT_LOCALE;
  const t = (key, params) => translate(l, key, params);
  t.locale = l;
  return t;
}

// Tentukan bahasa permintaan: header X-Locale → ?lang= → bahasa profil pengguna → lalai (ms).
// Accept-Language pelayar sengaja TIDAK digunakan supaya bahasa utama sentiasa Bahasa Melayu
// melainkan pengguna memilih bahasa lain secara jelas.
export function resolveLocale({ header, query, userLocale } = {}) {
  return normalizeLocale(header) || normalizeLocale(query) || normalizeLocale(userLocale) || DEFAULT_LOCALE;
}
